/**
 * Drafts API repository
 */

import { API_ENDPOINTS } from '../config/apiEndpoints';
import { fetchWithAuth } from '../utils/tokenRefresh';
import {
    Draft,
    DraftListItem,
    DraftDetailResponse,
    MakePickResponse,
    EditPickResponse,
    PrefillPickResponse,
    ClearPrefilledPickResponse,
    UpdateDescriptionResponse,
    Invitation,
    InvitationResponse
} from '../objects/draftTypes';

const DRAFTS_API = API_ENDPOINTS.DRAFTS;

export interface ApiError {
    error: string;
}

export const createDraft = async (
    name: string,
    drafters: string[],
    numRounds: number,
    notifyOnTurn: boolean = true,
    description: string = ''
): Promise<{ draft: Draft } | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'create_draft',
                name,
                drafters,
                num_rounds: numRounds,
                notify_on_turn: notifyOnTurn,
                description
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to create draft' };
    }
};

export const getDraft = async (draftUuid: string): Promise<DraftDetailResponse | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'get_draft',
                draft_uuid: draftUuid
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to get draft' };
    }
};

export const listMyDrafts = async (): Promise<{ drafts: DraftListItem[] } | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'list_my_drafts'
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to list drafts' };
    }
};

export const makePick = async (
    draftUuid: string,
    value: string
): Promise<MakePickResponse | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'make_pick',
                draft_uuid: draftUuid,
                value
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to make pick' };
    }
};

export const editPick = async (
    draftUuid: string,
    pickNumber: number,
    value: string
): Promise<EditPickResponse | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'edit_pick',
                draft_uuid: draftUuid,
                pick_number: pickNumber,
                value
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to edit pick' };
    }
};

export const prefillPick = async (
    draftUuid: string,
    pickNumber: number,
    value: string
): Promise<PrefillPickResponse | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'prefill_pick',
                draft_uuid: draftUuid,
                pick_number: pickNumber,
                value
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to prefill pick' };
    }
};

export const clearPrefilledPick = async (
    draftUuid: string,
    pickNumber: number
): Promise<ClearPrefilledPickResponse | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'clear_prefilled_pick',
                draft_uuid: draftUuid,
                pick_number: pickNumber
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to clear prefilled pick' };
    }
};

export const updateDescription = async (
    draftUuid: string,
    description: string
): Promise<UpdateDescriptionResponse | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'update_description',
                draft_uuid: draftUuid,
                description
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to update description' };
    }
};

export const listPendingInvitations = async (): Promise<{ invitations: Invitation[] } | ApiError> => {
    try {
        const response = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'list_pending_invitations'
            })
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to list invitations' };
    }
};

export const respondToInvitation = async (
    draftUuid: string,
    response: 'accept' | 'decline'
): Promise<InvitationResponse | ApiError> => {
    try {
        const apiResponse = await fetchWithAuth(`${DRAFTS_API}/drafts`, {
            method: 'POST',
            body: JSON.stringify({
                request_type: 'respond_to_invitation',
                draft_uuid: draftUuid,
                response
            })
        });

        const data = await apiResponse.json();

        if (!apiResponse.ok) {
            return { error: data.error || `HTTP ${apiResponse.status}` };
        }

        return data;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to respond to invitation' };
    }
};
